import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import { UserRole } from '@mirsad/shared';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { Button, Card, Loading } from '@/components/ui';

const Connexion = lazy(() => import('@/pages/auth/Connexion'));
const ChangerMotDePasse = lazy(() => import('@/pages/auth/ChangerMotDePasse'));
const Exploration = lazy(() => import('@/pages/exploration/Exploration'));
const PageArticle = lazy(() => import('@/pages/exploration/PageArticle'));
const Boutiques = lazy(() => import('@/pages/boutiques/Boutiques'));
const Avis = lazy(() => import('@/pages/avis/Avis'));
const Ventes = lazy(() => import('@/pages/ventes/Ventes'));
const Tendances = lazy(() => import('@/pages/analyses/Tendances'));
const Prix = lazy(() => import('@/pages/prix/Prix'));
const RapportsAvances = lazy(() => import('@/pages/rapports-avances/Historique'));
const GalerieModeles = lazy(() => import('@/pages/rapports-avances/GalerieModeles'));
const EditeurModele = lazy(() => import('@/pages/rapports-avances/EditeurModele'));
const AssistantCreation = lazy(() => import('@/pages/rapports-avances/AssistantCreation'));
const VisionneuseRapport = lazy(() => import('@/pages/rapports-avances/VisionneuseRapport'));
const Collectes = lazy(() => import('@/pages/collectes/Collectes'));
const Audit = lazy(() => import('@/pages/administration/Audit'));
const Comptes = lazy(() => import('@/pages/administration/Comptes'));
const Parametres = lazy(() => import('@/pages/administration/Parametres'));
const Methode = lazy(() => import('@/pages/methode/Methode'));

/** Enveloppe une page protégée dans la coquille applicative. */
const espace = (element: React.ReactNode, roles?: UserRole[]) => (
  <ProtectedRoute roles={roles}>
    <AppShell>
      <Suspense fallback={<Loading />}>{element}</Suspense>
    </AppShell>
  </ProtectedRoute>
);

/** Redirige en gardant la requête : un lien déjà partagé conserve son onglet et ses filtres. */
function RedirectionAvecRequete({ vers }: { vers: string }) {
  const { search } = useLocation();
  return <Navigate to={`${vers}${search}`} replace />;
}

function PageErreur({ code, titre, aide }: { code: string; titre: string; aide: string }) {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="max-w-md text-center">
        <p className="text-display text-primary">{code}</p>
        <h1 className="mt-2 text-h2">{titre}</h1>
        <p className="mt-2 text-small text-ink-muted">{aide}</p>
        <div className="mt-5">
          <Button onClick={() => window.location.assign('/')}>Retour à l'accueil</Button>
        </div>
      </Card>
    </div>
  );
}

const routeur = createBrowserRouter([
  { path: '/', element: <Navigate to="/tableau-de-bord" replace /> },

  {
    path: '/connexion',
    element: (
      <Suspense fallback={<Loading />}>
        <Connexion />
      </Suspense>
    ),
  },
  {
    path: '/changer-mot-de-passe',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <ChangerMotDePasse />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Le tableau de bord est la page Ventes et marchés (demande de Naoufal du
  // 11/09/2026). L'ancienne adresse y mène, avec son onglet et ses filtres.
  { path: '/tableau-de-bord', element: espace(<Ventes />) },
  { path: '/ventes', element: <RedirectionAvecRequete vers="/tableau-de-bord" /> },

  // `resolveHome` renvoie /analyses pour l'ANALYSTE : cette route doit exister,
  // sinon la connexion d'un analyste aboutit sur une page introuvable.
  { path: '/analyses', element: <Navigate to="/tendances" replace /> },

  // L'exploration a absorbé l'ancienne page Catalogue : l'adresse reste
  // valable pour les favoris et les liens déjà partagés.
  { path: '/catalogue', element: <Navigate to="/exploration" replace /> },
  { path: '/exploration', element: espace(<Exploration />) },
  { path: '/exploration/articles/:id', element: espace(<PageArticle />) },
  // Page Boutiques rétablie à part entière (demande de Naoufal du 11/09/2026) :
  // classement par ventes mesurées, distinct de la vue Boutiques de l'exploration.
  { path: '/boutiques', element: espace(<Boutiques />) },
  { path: '/boutiques/:id', element: espace(<Boutiques />) },
  // Avis : tous les avis, les avis faibles et leurs motifs, les thèmes (demande
  // de Naoufal du 11/09/2026). Lecture ouverte à tous les rôles, sur leur périmètre.
  { path: '/avis', element: espace(<Avis />) },
  // Ce que mesure chaque chiffre : les infobulles des écrans y renvoient.
  { path: '/methode', element: espace(<Methode />) },
  // La matrice ouvre les tendances au conseiller, sur ses familles ; pas à l'observateur.
  {
    path: '/tendances',
    element: espace(<Tendances />, [UserRole.ADMIN, UserRole.ANALYSTE, UserRole.CONSEILLER]),
  },
  // Prix : catalogue et ventes, que la matrice ouvre à tous, le conseiller sur ses familles.
  { path: '/prix', element: espace(<Prix />) },

  // Rapports avancés : génération par IA, réservée aux profils transverses.
  {
    path: '/rapports-avances',
    element: espace(<RapportsAvances />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/rapports-avances/modeles',
    element: espace(<GalerieModeles />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/rapports-avances/modeles/:id',
    element: espace(<EditeurModele />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/rapports-avances/nouveau-modele',
    element: espace(<EditeurModele />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/rapports-avances/nouveau',
    element: espace(<AssistantCreation />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/rapports-avances/:id',
    element: espace(<VisionneuseRapport />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },
  {
    path: '/collectes',
    element: espace(<Collectes />, [UserRole.ADMIN, UserRole.ANALYSTE]),
  },

  // L'espace d'administration ouvre sur les comptes.
  { path: '/administration', element: <Navigate to="/administration/comptes" replace /> },
  { path: '/administration/comptes', element: espace(<Comptes />, [UserRole.ADMIN]) },
  { path: '/administration/parametres', element: espace(<Parametres />, [UserRole.ADMIN]) },
  { path: '/administration/audit', element: espace(<Audit />, [UserRole.ADMIN]) },

  {
    path: '/acces-refuse',
    element: (
      <PageErreur
        code="403"
        titre="Accès refusé"
        aide="Votre rôle ne permet pas d'accéder à cette page."
      />
    ),
  },
  {
    path: '*',
    element: (
      <PageErreur
        code="404"
        titre="Page introuvable"
        aide="Cette page n'existe pas ou a été déplacée."
      />
    ),
  },
]);

export default function App() {
  return <RouterProvider router={routeur} />;
}
